import api from './authService';
import {
  UserListItem,
  CreateUserRequest,
  UpdateUserRequest,
  ChangePasswordRequest,
  UserFilters,
  UserRole,
  PendingUserListItem,
  UserApprovalRequest,
  UserRejectionRequest,
  BulkApprovalRequest
} from '../types/user';

/**
 * A `POST /users/{id}/reset-password` válasza. A szerver SZÁNDÉKOSAN nem ad vissza jelszót —
 * az új, véletlen ideiglenes jelszó csak a felhasználó e-mailjében jelenik meg.
 */
export interface PasswordResetResult {
  success: boolean;
  message: string;
  userId: string;
  userName: string;
  userEmail: string;
}

export const userService = {
  async getUsers(
    filters?: UserFilters,
    pagination?: { pageNumber: number; pageSize: number }
  ): Promise<{ data: UserListItem[]; totalCount?: number }> {
    const params = new URLSearchParams();
    if (filters?.search) params.append('search', filters.search);
    if (filters?.roleId) params.append('roleId', filters.roleId);
    if (filters?.venueId) params.append('venueId', filters.venueId);
    if (filters?.isActive !== undefined) params.append('isActive', filters.isActive.toString());
    if (filters?.isApproved !== undefined) params.append('isApproved', filters.isApproved.toString());

    // Add pagination parameters
    if (pagination) {
      params.append('pageNumber', pagination.pageNumber.toString());
      params.append('pageSize', pagination.pageSize.toString());
    }

    const response = await api.get<any>(`/users?${params}`);

    // Handle both paginated and non-paginated responses
    if (pagination && response.data.data) {
      return {
        data: response.data.data,
        totalCount: response.data.totalCount
      };
    }

    // Backwards compatibility - return simple array
    return { data: response.data };
  },

  async getCoaches(): Promise<UserListItem[]> {
    const response = await api.get('/users/coaches');
    // A szerver `UserDto`-t ad (UsersController.GetCoaches). A leképezés korábban ELDOBTA a
    // `hourlyRate`-et, amit a kifizetés-képernyő kiír (CoachPaymentManagement) → az óradíj
    // SOHA nem látszott. A `userRoleName` mező pedig nem is létezik a `UserDto`-n
    // (`userRole` = rendszernév, `userRoleDisplayName` = megjelenítendő név).
    return response.data.map((coach: any) => ({
      userId: coach.userId,
      name: coach.name,
      email: coach.email,
      userRole: coach.userRole,
      userRoleDisplayName: coach.userRoleDisplayName ?? coach.userRole,
      hourlyRate: coach.hourlyRate,
      isActive: coach.isActive
    }));
  },

  // Lightweight, player-accessible coach list (id + name only) for pickers such as the
  // private-lesson request. Unlike getCoaches() this endpoint is not restricted to admins/coaches.
  async getCoachesBasic(): Promise<{ userId: string; name: string }[]> {
    const response = await api.get('/users/coaches-basic');
    return response.data;
  },

  async getUser(id: string): Promise<UserListItem> {
    const response = await api.get<UserListItem>(`/users/${id}`);
    return response.data;
  },

  async createUser(userData: CreateUserRequest): Promise<UserListItem> {
    const response = await api.post<UserListItem>('/users', userData);
    return response.data;
  },

  async updateUser(id: string, userData: UpdateUserRequest): Promise<UserListItem> {
    const response = await api.put<UserListItem>(`/users/${id}`, userData);
    return response.data;
  },

  async deleteUser(id: string): Promise<void> {
    await api.delete(`/users/${id}`);
  },

  /**
   * Inaktív felhasználó VISSZAENGEDÉSE — CSAK az `isActive`-ot állítja [BL-A, 2026-09-15].
   *
   * ⚠️ SOHA NE cseréld ezt vissza `updateUser`-re részleges törzzsel. A `PUT /api/users/{id}` a
   * teljes felhasználót írja felül, tehát a kérésből KIMARADÓ mezőket a szerver `null`-ra állítja
   * (`backend/PinyoApi/Controllers/UsersController.cs`, `UpdateUser`). MÉRVE élő lokális API-n: az
   * „Aktiválás" ikon egyetlen kattintására egy edző óradíja (`hourlyRate 4200 → null`) és
   * bankszámlaszáma némán eltűnt, miközben a felület „A felhasználó aktiválva."-t jelzett.
   * A `POST /users/{id}/reactivate` kizárólag az `IsActive = true`-t írja, és auditnaplóz.
   */
  async reactivateUser(id: string): Promise<void> {
    await api.post(`/users/${id}/reactivate`, {});
  },

  async changePassword(id: string, passwordData: ChangePasswordRequest): Promise<void> {
    await api.post(`/users/${id}/change-password`, passwordData);
  },

  async getUserRoles(): Promise<UserRole[]> {
    const response = await api.get<UserRole[]>('/auth/user-roles');
    return response.data;
  },

  // Pending user approval methods
  async getPendingUsers(): Promise<PendingUserListItem[]> {
    const response = await api.get<PendingUserListItem[]>('/users/pending');
    return response.data;
  },

  async approveUser(userData: UserApprovalRequest): Promise<void> {
    await api.post(`/users/${userData.userId}/approve`, {});
  },

  async rejectUser(userData: UserRejectionRequest): Promise<void> {
    await api.post(`/users/${userData.userId}/reject`, {
      reason: userData.reason
    });
  },

  async bulkApproveUsers(userData: BulkApprovalRequest): Promise<void> {
    await api.post('/users/bulk-approve', {
      userIds: userData.userIds
    });
  },

  /**
   * Jelszó alaphelyzetbe állítása (admin). A szerver VÉLETLEN ideiglenes jelszót generál, és
   * KIZÁRÓLAG e-mailben küldi el a felhasználónak — a válaszban jelszó NINCS (és tudatosan nem
   * is lesz: `UsersController.ResetPassword`). A válaszból csak azt lehet a felületen kiírni,
   * hogy MELY címre ment a levél.
   */
  async resetPassword(userId: string): Promise<PasswordResetResult> {
    const response = await api.post<PasswordResetResult>(`/users/${userId}/reset-password`, {});
    return response.data;
  },
};